/* eslint-disable import/extensions */
/* eslint-disable global-require */
/* eslint-disable import/no-unresolved */
import React, { ReactElement, ReactChild } from "react";
import styled from "styled-components";

import { theme } from "../theme/theme";

export interface ChipProps {
  borderRadius?: "medium" | "small";
  boxShadow?: "medium" | "extraMedium" | "large" | null;
  children: ReactChild;
  className?: string;
  size?: "medium" | "small";
  translucent?: boolean;
  variant:
    | "blue"
    | "gradient"
    | "grayDark"
    | "grayLight"
    | "grayMedium"
    | "green"
    | "orange"
    | "purple"
    | "red"
    | "white"
    | "whiteOnRed"
    | "yellow"
    | "yellowDark";
}

const getBorderRadius = (variant, radius): string => {
  let borderRadius;

  if (variant === "gradient") {
    borderRadius = theme.radius.s;
  }

  switch (radius) {
    case "small":
      borderRadius = theme.radius.s;
      break;
    case "medium":
      borderRadius = theme.radius.m;
      break;
    default:
      borderRadius = theme.radius.s;
      break;
  }

  return borderRadius;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getVariant = (variant): Array<any> => {
  let chipColor;
  let textColor;

  switch (variant) {
    case "blue":
      chipColor = theme.color.blueLightest;
      textColor = theme.color.blueDarkest;
      break;
    case "gradient":
      chipColor = theme.gradients.aqua;
      textColor = theme.color.black;
      break;
    case "grayDark":
      chipColor = theme.color.grayDarkest;
      textColor = theme.color.white;
      break;
    case "grayLight":
      chipColor = theme.color.grayLightest;
      textColor = theme.color.grayDarkest;
      break;
    case "grayMedium":
      chipColor = theme.color.gray;
      textColor = theme.color.white;
      break;
    case "green":
      chipColor = theme.color.greenLightest;
      textColor = theme.color.greenDarkest;
      break;
    case "orange":
      chipColor = theme.color.orange;
      textColor = theme.color.white;
      break;
    case "purple":
      chipColor = theme.color.purpleLight;
      textColor = theme.color.purple;
      break;
    case "red":
      chipColor = theme.color.redLightest;
      textColor = theme.color.black;
      break;
    case "white":
      chipColor = theme.color.white;
      textColor = theme.color.grayDarkest;
      break;
    case "whiteOnRed":
      chipColor = theme.color.red;
      textColor = theme.color.white;
      break;
    case "yellow":
      chipColor = theme.color.yellowLight;
      textColor = theme.color.yellowDarkest;
      break;
    case "yellowDark":
      chipColor = theme.color.yellowDark;
      textColor = theme.color.yellowLightest;
      break;
    default:
      chipColor = theme.gradients.aqua;
      textColor = theme.color.white;
      break;
  }
  return [chipColor, textColor];
};

function getBoxShadow(boxShadow): string {
  switch (boxShadow) {
    case "large":
      return theme.boxShadow.l;
    case "medium":
      return theme.boxShadow.m;
    case "extraMedium":
      return theme.boxShadow.extraMedium;
    default:
      return null;
  }
}

const StyledChip = styled.div<ChipProps>`
  border-radius: ${({ variant, borderRadius }): string => {
    return getBorderRadius(variant, borderRadius);
  }};
  box-shadow: ${({ boxShadow }): string => {
    return getBoxShadow(boxShadow);
  }};
  background: ${({ variant }): string => {
    return getVariant(variant)[0];
  }};
  color: ${({ variant }): string => {
    return getVariant(variant)[1];
  }};
  display: inline-flex;
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.superBold};
  line-height: ${theme.lineHeight.s};
  padding: ${theme.spacing.xxxxs} 10px;
  text-transform: uppercase;
  letter-spacing: ${theme.letterSpacing.s};
  white-space: nowrap;

  mix-blend-mode: multiply;
  opacity: 0.8;

  width: fit-content;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Chip = ({ children, variant, className }: ChipProps): ReactElement => {
  return (
    <StyledChip className={className} variant={variant}>
      {children}
    </StyledChip>
  );
};

Chip.defaultProps = {
  className: "",
};

export default Chip;
